/**
 * Sysadmin System Library hooks — PR-J.
 *
 * Wraps `/api/v1/admin/platform/library/*`. Used by the sysadmin UI
 * at `/sysadmin/library/*` to manage system rows (organization_id IS
 * NULL) for folders, articles, tags, documents, document folders, and
 * sources, plus the publish lifecycle.
 *
 * Tenant-side System Library reads (badged as "System" inside
 * /library) use `use-tenant-system-library.ts` — a separate hook that
 * targets `/api/v1/library/system/*` with redaction baked in.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

const BASE = '/admin/platform/library';

export type LifecycleStatus = 'draft' | 'published' | 'deprecated' | 'retired';
export type LibraryEntity =
  | 'folder'
  | 'article'
  | 'tag'
  | 'document'
  | 'source';

// ─── Wire shapes ───────────────────────────────────────────────────────────

export interface SysLibFolder {
  id: string;
  organizationId: string | null;
  name: string;
  slug: string | null;
  description: string | null;
  parentId: string | null;
  sortOrder: number;
  icon: string | null;
  color: string | null;
  sensitivity: string;
  exportControl: string;
  isDefault: boolean;
  status: LifecycleStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SysLibArticle {
  id: string;
  organizationId: string | null;
  title: string;
  slug: string | null;
  content: string;
  contentText: string;
  excerpt: string | null;
  folderId: string | null;
  parentId: string | null;
  status: LifecycleStatus;
  version: number;
  authorId: string;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  viewCount: number;
  sensitivity: string;
  exportControl: string;
  createdAt: string;
  updatedAt: string;
}

export interface SysLibTag {
  id: string;
  organizationId: string | null;
  name: string;
  color: string;
  sortOrder: number;
  status: LifecycleStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  createdAt: string;
}

export interface SysLibDocument {
  id: string;
  organizationId: string | null;
  folderId: string | null;
  title: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  status: string; // ingestion status
  classification: string | null;
  documentType: string | null;
  description: string | null;
  chunkCount: number | null;
  publishStatus: LifecycleStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  sensitivity: string;
  exportControl: string;
  createdAt: string;
  updatedAt: string;
}

export interface SysLibSource {
  id: string;
  organizationId: string | null;
  connectorKind: string;
  displayName: string;
  config: Record<string, unknown>;
  syncSchedule: string;
  syncEnabled: boolean;
  isActive: boolean;
  status: LifecycleStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  createdAt: string;
  updatedAt: string;
}

const KEYS = {
  folders: (status?: LifecycleStatus) => ['sysadmin-library', 'folders', status ?? 'all'] as const,
  articles: (status?: LifecycleStatus, folderId?: string) =>
    ['sysadmin-library', 'articles', status ?? 'all', folderId ?? 'all'] as const,
  article: (id: string) => ['sysadmin-library', 'article', id] as const,
  tags: (status?: LifecycleStatus) => ['sysadmin-library', 'tags', status ?? 'all'] as const,
  documents: (status?: LifecycleStatus, folderId?: string) =>
    ['sysadmin-library', 'documents', status ?? 'all', folderId ?? 'all'] as const,
  sources: (status?: LifecycleStatus) => ['sysadmin-library', 'sources', status ?? 'all'] as const,
};

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['sysadmin-library'] });
}

// ─── Folders ───────────────────────────────────────────────────────────────

// Bug-5 (2026-05-10 catalog smoke triage): server envelope flipped
// `data` → `items` to match every other catalog leaf (catalog-tools,
// catalog-skills, catalog-agents, system-catalog, etc.). Web hooks
// updated in lockstep. Consumer code that accessed `.data.data` must
// now read `.data.items`.
//
// Decision 18 — the System Library has ONE folder set (ai_document_folders,
// organization_id NULL). Every sysadmin screen that LISTS folders (the
// Documents folder filter and the edit dialogs, the upload dialog's picker)
// reads it through this hook; the agent builder's Library references read the
// same route with `?status=published`. The API's second family,
// `/document-folders`, is deleted (LB2-T3); `/folders` is the only one.
export function useSysLibFolders(status?: LifecycleStatus, opts: { enabled?: boolean } = {}) {
  return useQuery<{ items: SysLibFolder[] }, Error>({
    queryKey: KEYS.folders(status),
    queryFn: () =>
      apiClient.get<{ items: SysLibFolder[] }>(
        `${BASE}/folders${status ? `?status=${status}` : ''}`,
      ),
    enabled: opts.enabled ?? true,
  });
}

export function useCreateSysLibFolder() {
  const qc = useQueryClient();
  return useMutation<SysLibFolder, Error, Partial<SysLibFolder> & { name: string }>({
    mutationFn: (body) => apiClient.post<SysLibFolder>(`${BASE}/folders`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSysLibFolder(id: string) {
  const qc = useQueryClient();
  return useMutation<SysLibFolder, Error, Partial<SysLibFolder>>({
    mutationFn: (body) =>
      apiClient.request<SysLibFolder>(`${BASE}/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSysLibFolder() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete<void>(`${BASE}/folders/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePublishSysLibFolder() {
  const qc = useQueryClient();
  return useMutation<SysLibFolder, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.post<SysLibFolder>(`${BASE}/folders/${id}/publish`, {}),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnpublishSysLibFolder() {
  const qc = useQueryClient();
  return useMutation<
    SysLibFolder,
    Error,
    { id: string; reason: string; nextStatus: 'deprecated' | 'retired' }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<SysLibFolder>(`${BASE}/folders/${id}/unpublish`, { reason, nextStatus }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ─── Articles ──────────────────────────────────────────────────────────────

export function useSysLibArticles(opts?: { status?: LifecycleStatus; folderId?: string }) {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.folderId) qs.set('folderId', opts.folderId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  // Bug-5: server envelope is `items` (was `data`).
  return useQuery<{ items: SysLibArticle[] }, Error>({
    queryKey: KEYS.articles(opts?.status, opts?.folderId),
    queryFn: () => apiClient.get<{ items: SysLibArticle[] }>(`${BASE}/articles${suffix}`),
  });
}

export function useSysLibArticle(id: string | null | undefined) {
  return useQuery<SysLibArticle, Error>({
    queryKey: id ? KEYS.article(id) : ['__skip__'],
    queryFn: () => apiClient.get<SysLibArticle>(`${BASE}/articles/${id}`),
    enabled: !!id,
  });
}

export function useCreateSysLibArticle() {
  const qc = useQueryClient();
  return useMutation<SysLibArticle, Error, Partial<SysLibArticle> & { title: string }>({
    mutationFn: (body) => apiClient.post<SysLibArticle>(`${BASE}/articles`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSysLibArticle(id: string) {
  const qc = useQueryClient();
  return useMutation<SysLibArticle, Error, Partial<SysLibArticle>>({
    mutationFn: (body) =>
      apiClient.request<SysLibArticle>(`${BASE}/articles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSysLibArticle() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete<void>(`${BASE}/articles/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePublishSysLibArticle() {
  const qc = useQueryClient();
  return useMutation<SysLibArticle, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.post<SysLibArticle>(`${BASE}/articles/${id}/publish`, {}),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnpublishSysLibArticle() {
  const qc = useQueryClient();
  return useMutation<
    SysLibArticle,
    Error,
    { id: string; reason: string; nextStatus: 'deprecated' | 'retired' }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<SysLibArticle>(`${BASE}/articles/${id}/unpublish`, { reason, nextStatus }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ─── Tags ──────────────────────────────────────────────────────────────────

export function useSysLibTags(status?: LifecycleStatus) {
  // Bug-5: server envelope is `items` (was `data`).
  return useQuery<{ items: SysLibTag[] }, Error>({
    queryKey: KEYS.tags(status),
    queryFn: () =>
      apiClient.get<{ items: SysLibTag[] }>(`${BASE}/tags${status ? `?status=${status}` : ''}`),
  });
}

export function useCreateSysLibTag() {
  const qc = useQueryClient();
  return useMutation<SysLibTag, Error, { name: string; color?: string; sortOrder?: number }>({
    mutationFn: (body) => apiClient.post<SysLibTag>(`${BASE}/tags`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSysLibTag(id: string) {
  const qc = useQueryClient();
  return useMutation<SysLibTag, Error, Partial<SysLibTag>>({
    mutationFn: (body) =>
      apiClient.request<SysLibTag>(`${BASE}/tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSysLibTag() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete<void>(`${BASE}/tags/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePublishSysLibTag() {
  const qc = useQueryClient();
  return useMutation<SysLibTag, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.post<SysLibTag>(`${BASE}/tags/${id}/publish`, {}),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnpublishSysLibTag() {
  const qc = useQueryClient();
  return useMutation<
    SysLibTag,
    Error,
    { id: string; reason: string; nextStatus: 'deprecated' | 'retired' }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<SysLibTag>(`${BASE}/tags/${id}/unpublish`, { reason, nextStatus }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ─── Documents ─────────────────────────────────────────────────────────────

export function useSysLibDocuments(opts?: { publishStatus?: LifecycleStatus; folderId?: string }) {
  const qs = new URLSearchParams();
  if (opts?.publishStatus) qs.set('publishStatus', opts.publishStatus);
  if (opts?.folderId) qs.set('folderId', opts.folderId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  // Bug-5: server envelope is `items` (was `data`).
  return useQuery<{ items: SysLibDocument[] }, Error>({
    queryKey: KEYS.documents(opts?.publishStatus, opts?.folderId),
    queryFn: () => apiClient.get<{ items: SysLibDocument[] }>(`${BASE}/documents${suffix}`),
  });
}

/**
 * Multipart upload of a system document — POST /admin/platform/library/documents/upload.
 *
 * Mirrors the tenant `uploadFile('/knowledge/documents/upload', formData)`
 * pattern but force-scopes to system rows (organization_id IS NULL,
 * publishStatus = 'draft'). Resolves to the newly created system
 * document (status='processing'); ingestion completes asynchronously.
 *
 * Callers should invalidate the documents list after success — done
 * by default through the React Query mutation onSuccess hook below.
 */
export function useUploadSysLibDocument() {
  const qc = useQueryClient();
  return useMutation<SysLibDocument, Error, FormData>({
    mutationFn: async (formData: FormData) => {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/v1${BASE}/documents/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({
          error: { message: `Upload failed (HTTP ${res.status})` },
        }));
        const message =
          (body as { error?: { message?: string } }).error?.message ??
          `Upload failed (HTTP ${res.status})`;
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSysLibDocument(id: string) {
  const qc = useQueryClient();
  return useMutation<SysLibDocument, Error, Partial<SysLibDocument>>({
    mutationFn: (body) =>
      apiClient.request<SysLibDocument>(`${BASE}/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSysLibDocument() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete<void>(`${BASE}/documents/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePublishSysLibDocument() {
  const qc = useQueryClient();
  return useMutation<SysLibDocument, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.post<SysLibDocument>(`${BASE}/documents/${id}/publish`, {}),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnpublishSysLibDocument() {
  const qc = useQueryClient();
  return useMutation<
    SysLibDocument,
    Error,
    { id: string; reason: string; nextStatus: 'deprecated' | 'retired' }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<SysLibDocument>(`${BASE}/documents/${id}/unpublish`, {
        reason,
        nextStatus,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ─── Sources ───────────────────────────────────────────────────────────────

export function useSysLibSources(status?: LifecycleStatus) {
  // Bug-5: server envelope is `items` (was `data`).
  return useQuery<{ items: SysLibSource[] }, Error>({
    queryKey: KEYS.sources(status),
    queryFn: () =>
      apiClient.get<{ items: SysLibSource[] }>(
        `${BASE}/sources${status ? `?status=${status}` : ''}`,
      ),
  });
}

export function useCreateSysLibSource() {
  const qc = useQueryClient();
  return useMutation<SysLibSource, Error, Partial<SysLibSource> & { connectorKind: string; displayName: string }>({
    mutationFn: (body) => apiClient.post<SysLibSource>(`${BASE}/sources`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSysLibSource(id: string) {
  const qc = useQueryClient();
  return useMutation<SysLibSource, Error, Partial<SysLibSource>>({
    mutationFn: (body) =>
      apiClient.request<SysLibSource>(`${BASE}/sources/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSysLibSource() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete<void>(`${BASE}/sources/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePublishSysLibSource() {
  const qc = useQueryClient();
  return useMutation<SysLibSource, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.post<SysLibSource>(`${BASE}/sources/${id}/publish`, {}),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnpublishSysLibSource() {
  const qc = useQueryClient();
  return useMutation<
    SysLibSource,
    Error,
    { id: string; reason: string; nextStatus: 'deprecated' | 'retired' }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<SysLibSource>(`${BASE}/sources/${id}/unpublish`, { reason, nextStatus }),
    onSuccess: () => invalidateAll(qc),
  });
}

// ─── Bulk publish ──────────────────────────────────────────────────────────

export function useBulkPublishSysLib() {
  const qc = useQueryClient();
  return useMutation<
    { entity: string; published: number; ids: string[] },
    Error,
    { entity: LibraryEntity; ids: string[] }
  >({
    mutationFn: (body) =>
      apiClient.post<{ entity: string; published: number; ids: string[] }>(
        `${BASE}/publish`,
        body,
      ),
    onSuccess: () => invalidateAll(qc),
  });
}
